/**
 * Global "Gemmer data …"-indikator (Charlie session 49).
 *
 * Reaktiv store + en window.fetch-wrapper der AUTOMATISK driver en slide-up i
 * footeren (SavingIndicator.vue) under enhver MUTERENDE API-kald (POST/PUT/
 * PATCH/DELETE mod /api). Fordi hver view og composable til sidst kalder
 * window.fetch — uanset om de bruger en lokal api()- eller fetchJson-helper —
 * fanger ÉT globalt interceptor-punkt ALLE steder hvor man opretter, retter
 * eller sletter data, uden at skulle røre hver enkelt kald-side. Nye sider får
 * indikatoren gratis.
 *
 * Concurrency: en tæller holder status i 'saving' indtil det SIDSTE samtidige
 * kald er færdigt (fx bulk-cell-gem). Når tælleren rammer 0 vises et kort
 * 'Gemt'-tail (eller 'Kunne ikke gemme' ved fejl), hvorefter den glider væk.
 *
 * Opt-out: sæt headeren 'x-cockpit-silent: 1' på et kald der har sin egen
 * progress-UI (fx Sync data-kaskaden), så det ikke også trigger footer-baren.
 */

import { reactive } from 'vue';

export type SaveStatus = 'idle' | 'saving' | 'done' | 'error';

export const savingState = reactive({
  /** Antal igangværende muterende kald (concurrency-tæller). */
  active: 0,
  status: 'idle' as SaveStatus,
  label: 'Gemmer data …',
  /** Tail-tekst ved succes/fejl — afhænger af om det var et gem eller en
   *  genindlæsning (load). Sættes af beginSave/beginLoad. */
  doneLabel: 'Gemt',
  errorLabel: 'Kunne ikke gemme',
  errorMsg: '',
  /**
   * ESTIMERET fremdrift 0-100. Disse kald har ingen ægte progress-events (det
   * langsomme er server-beregningen, ikke download), så procenten er en jævn
   * model: den klatrer aftagende mod et 90 %-loft mens kaldet kører og springer
   * til 100 % når svaret lander. Visuel feedback — ikke et målt tal ([D-107]).
   */
  percent: 0,
});

const PROGRESS_CAP = 90; // procent-loft mens kaldet stadig kører
let resetTimer: ReturnType<typeof setTimeout> | null = null;
let progressTimer: ReturnType<typeof setInterval> | null = null;

function clearResetTimer(): void {
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = null;
  }
}

function startProgress(): void {
  if (progressTimer) return;
  progressTimer = setInterval(() => {
    if (savingState.percent < PROGRESS_CAP) {
      // Decelererende klatring: store spring tidligt, små til sidst.
      const step = Math.max(0.4, (PROGRESS_CAP - savingState.percent) * 0.12);
      savingState.percent = Math.min(PROGRESS_CAP, savingState.percent + step);
    }
  }, 180);
}

function stopProgress(): void {
  if (progressTimer) {
    clearInterval(progressTimer);
    progressTimer = null;
  }
}

/** Markér start på et muterende kald (eller en genindlæsning via beginLoad). */
export function beginSave(
  label = 'Gemmer data …',
  opts?: { doneLabel?: string; errorLabel?: string },
): void {
  clearResetTimer();
  if (savingState.active === 0) savingState.percent = 8; // start lidt inde → føles responsivt
  savingState.active += 1;
  savingState.label = label;
  savingState.doneLabel = opts?.doneLabel ?? 'Gemt';
  savingState.errorLabel = opts?.errorLabel ?? 'Kunne ikke gemme';
  savingState.status = 'saving';
  startProgress();
}

/**
 * Som beginSave, men for en LÆSE-genindlæsning (fx et filter-skift der re-fetcher
 * en GET). Samme footer-bar, blot med "Opdaterer …"/"Opdateret"-tekst. Bruges
 * manuelt rundt om et GET-kald, da interceptoren kun auto-tracker muterende kald.
 * Afsluttes med endSave(ok, msg).
 */
export function beginLoad(
  label = 'Opdaterer …',
  opts?: { doneLabel?: string; errorLabel?: string },
): void {
  beginSave(label, {
    doneLabel: opts?.doneLabel ?? 'Opdateret',
    errorLabel: opts?.errorLabel ?? 'Kunne ikke opdatere',
  });
}

/** Markér slut. Når alle samtidige kald er færdige vises et kort status-tail. */
export function endSave(ok: boolean, errorMsg = ''): void {
  savingState.active = Math.max(0, savingState.active - 1);
  if (savingState.active > 0) return; // andre saves kører stadig → bliv i 'saving'
  stopProgress();
  savingState.status = ok ? 'done' : 'error';
  savingState.errorMsg = ok ? '' : errorMsg;
  if (ok) savingState.percent = 100; // fuldfør baren ved succes
  clearResetTimer();
  resetTimer = setTimeout(
    () => {
      // Kun nulstil hvis der ikke er startet et nyt save i mellemtiden.
      if (savingState.active === 0) {
        savingState.status = 'idle';
        savingState.errorMsg = '';
        savingState.percent = 0;
      }
    },
    ok ? 1200 : 4500,
  );
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function methodOf(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (typeof Request !== 'undefined' && input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.pathname;
  if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
  return String(input);
}

function isSilent(input: RequestInfo | URL, init?: RequestInit): boolean {
  const fromInit = init?.headers;
  const fromReq =
    typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined;
  const h = new Headers(fromInit ?? fromReq);
  return h.get('x-cockpit-silent') === '1';
}

// ---- Side-load-tracking (initial load pr. route, [D-134]) ------------------
// Den SAMME footer-bar vises også når en side henter sine FØRSTE data efter en
// route-navigation — generelt for ALLE sider. Drevet af router-hooket
// startPageLoad() (beforeEach) + GET /api-kald i interceptoren. Kun den
// initiale load-batch tælles: når netværket er idle lukkes vinduet, så senere
// lazy/bruger-initierede GETs (drill, autocomplete) IKKE rører side-load-baren
// (de kan vise baren via deres egne beginLoad-kald). En vis-forsinkelse gør at
// LETTE sider (svar < SHOW_DELAY) aldrig flasher baren; tunge sider (fx
// /items/discounts) viser den indtil dataene lander.
let plActive = false; // et navigations-vindue er åbent
let plInFlight = 0; // antal in-flight GET /api i vinduet
let plShown = false; // har vi faktisk vist baren?
let plOk = true; // AND af alle svars ok-status
let plNoReqTimer: ReturnType<typeof setTimeout> | null = null;
let plShowTimer: ReturnType<typeof setTimeout> | null = null;
let plIdleTimer: ReturnType<typeof setTimeout> | null = null;

const PL_NO_REQUEST_MS = 600; // statisk side uden api-kald → vis aldrig baren
const PL_SHOW_DELAY_MS = 250; // hurtige sider flasher ikke
const PL_IDLE_MS = 200; // network-idle før vinduet lukkes

function plClearTimers(): void {
  for (const t of [plNoReqTimer, plShowTimer, plIdleTimer]) if (t) clearTimeout(t);
  plNoReqTimer = plShowTimer = plIdleTimer = null;
}

/**
 * Kald fra router (beforeEach) ved hver navigation, inkl. første app-load.
 * Åbner et side-load-vindue; baren vises kun hvis et GET /api-kald er in-flight
 * efter vis-forsinkelsen.
 */
export function startPageLoad(): void {
  // Ny navigation midt i en igangværende (vist) side-load → luk den rent først.
  // Betingelsen er KUN plShown (ikke plActive): så længe vi har en åben bar
  // SKAL den lukkes (endSave), ellers lækker concurrency-tælleren hvis en side
  // re-navigerer (fx URL-query-sync) mens baren er vist. Invariant: plShown===
  // true ⟺ præcis én åben side-load-bar i tælleren.
  if (plShown) endSave(true);
  plClearTimers();
  plActive = true;
  plInFlight = 0;
  plShown = false;
  plOk = true;
  plNoReqTimer = setTimeout(() => {
    if (plActive && plInFlight === 0 && !plShown) {
      plActive = false;
      plClearTimers();
    }
  }, PL_NO_REQUEST_MS);
}

function plOnGetBegin(): void {
  if (plNoReqTimer) {
    clearTimeout(plNoReqTimer);
    plNoReqTimer = null;
  }
  if (plIdleTimer) {
    clearTimeout(plIdleTimer);
    plIdleTimer = null;
  }
  plInFlight += 1;
  if (!plShown && !plShowTimer) {
    plShowTimer = setTimeout(() => {
      plShowTimer = null;
      if (plActive && plInFlight > 0 && !plShown) {
        plShown = true;
        beginLoad('Indlæser side …', { doneLabel: 'Indlæst', errorLabel: 'Kunne ikke indlæse' });
      }
    }, PL_SHOW_DELAY_MS);
  }
}

function plOnGetEnd(ok: boolean): void {
  plOk = plOk && ok;
  plInFlight = Math.max(0, plInFlight - 1);
  if (plInFlight > 0) return;
  if (plIdleTimer) clearTimeout(plIdleTimer);
  plIdleTimer = setTimeout(() => {
    plIdleTimer = null;
    if (!plActive || plInFlight > 0) return;
    if (plShown) endSave(plOk, plOk ? '' : 'Kunne ikke indlæse');
    else if (plShowTimer) {
      clearTimeout(plShowTimer);
      plShowTimer = null;
    }
    plActive = false;
    plShown = false;
  }, PL_IDLE_MS);
}

let installed = false;

/**
 * Installér den globale fetch-wrapper. Kald ÉN gang i main.ts FØR app.mount().
 * Idempotent. Muterende /api-kald driver gem-baren; GET /api-kald tælles kun
 * når et side-load-vindue er åbent ([D-134]); øvrige kald passerer urørt.
 */
export function installSavingFetchInterceptor(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const orig = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const method = methodOf(input, init);
    const isApi = urlOf(input).includes('/api');
    const silent = isSilent(input, init);
    const mutating = MUTATING.has(method) && isApi && !silent;
    const pageGet = method === 'GET' && isApi && !silent && plActive;
    if (mutating) beginSave();
    if (pageGet) plOnGetBegin();
    try {
      const res = await orig(input, init);
      if (mutating) endSave(res.ok, res.ok ? '' : `Serverfejl (${res.status})`);
      if (pageGet) plOnGetEnd(res.ok);
      return res;
    } catch (err) {
      if (mutating) endSave(false, err instanceof Error ? err.message : 'Netværksfejl');
      if (pageGet) plOnGetEnd(false);
      throw err;
    }
  };
}
